import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Checkbox } from '../ui/checkbox';
import { Label } from '../ui/label';
import { Loader2, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import { useInstallationPlan } from '../../hooks/useTemplates';
import { templateService } from '../../services/template.service';
import BackgroundService from '../../services/bg.service';
import type { Template } from '../../services/template.service';

type UpgradeState = 'IDLE' | 'IN_PROGRESS' | 'COMPLETE';

interface UpgradeAllDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  template: Template | null;
}

export function UpgradeAllDialog({ open, onOpenChange, template }: UpgradeAllDialogProps) {
  const [publishToProduction, setPublishToProduction] = useState(false);
  const [state, setState] = useState<UpgradeState>('IDLE');
  const queryClient = useQueryClient();
  const { data: plan } = useInstallationPlan(template?.id);

  const handleUpgrade = async () => {
    if (!template) return;
    setState('IN_PROGRESS');
    try {
      const result = await templateService.upgradeAllInstallations(template.id, {
        publish_to_production: publishToProduction,
      });
      await BackgroundService.getInstance().waitForJob(result.job_id);
      setState('COMPLETE');
      queryClient.invalidateQueries({ queryKey: ['template-installations'] });
      queryClient.invalidateQueries({ queryKey: ['templates'] });
      toast.success('All installations upgraded successfully');
    } catch (err: any) {
      toast.error(err.message || 'Failed to upgrade installations');
      setState('IDLE');
    }
  };

  const handleClose = () => {
    if (state !== 'IN_PROGRESS') {
      setState('IDLE');
      setPublishToProduction(false);
      onOpenChange(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            Upgrade All Installations — {template?.name} (v{template?.version})
          </DialogTitle>
          <DialogDescription>
            This will upgrade all accounts that have this template installed.
          </DialogDescription>
        </DialogHeader>

        {state === 'IDLE' && plan && (
          <div className="py-4 space-y-4">
            <div className="text-sm space-y-1">
              <p>Total installations: <strong>{plan.total}</strong></p>
              <p>At current version: <strong>{plan.at_next_version}</strong></p>
              <p>Below current version: <strong>{plan.below_next_version}</strong></p>
              {plan.no_version > 0 && (
                <p>No version set: <strong>{plan.no_version}</strong></p>
              )}
            </div>

            <div className="flex items-center space-x-2">
              <Checkbox
                id="upgrade-develop"
                checked={true}
                disabled={true}
              />
              <Label htmlFor="upgrade-develop" className="text-sm">
                Upgrade template in Develop Mode
              </Label>
            </div>
            <div className="flex items-center space-x-2">
              <Checkbox
                id="publish-to-production"
                checked={publishToProduction}
                onCheckedChange={(checked) => setPublishToProduction(checked === true)}
              />
              <Label htmlFor="publish-to-production" className="text-sm">
                and publish to Production
              </Label>
            </div>
          </div>
        )}

        {state === 'IN_PROGRESS' && (
          <div className="flex flex-col items-center justify-center py-8 gap-3">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
            <p className="text-sm text-muted-foreground">Upgrading installations...</p>
          </div>
        )}

        {state === 'COMPLETE' && (
          <div className="flex flex-col items-center justify-center py-8 gap-3">
            <CheckCircle2 className="h-8 w-8 text-green-500" />
            <p className="text-sm text-muted-foreground">All installations upgraded successfully.</p>
          </div>
        )}

        <DialogFooter>
          {state === 'IDLE' && (
            <>
              <Button variant="outline" onClick={handleClose}>Cancel</Button>
              <Button onClick={handleUpgrade} disabled={!plan}>
                Upgrade All
              </Button>
            </>
          )}
          {state === 'COMPLETE' && (
            <Button onClick={handleClose}>Done</Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
